import { useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import React, { Dispatch, SetStateAction, useState } from "react";
import { AvailableLanguages, T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { v4 } from "uuid";
import { Input } from "../InputCategories";

const availableLanguagesOptions = Object.values(AvailableLanguages).map(lang => ({
  label: lang.toLocaleUpperCase(),
  value: lang,
}));

const GeneralSubForm = (props: {
  dashboard: Dashboard;
  setNewGeneralSub: Dispatch<SetStateAction<object>>;
  cancel: () => void;
}) => {
  const [title, setTitle] = useState<string>(props.dashboard.title);
  const [companyShared, setCompanyShared] = useState<boolean>(props.dashboard.companyShared);
  const [language, setLanguage] = useState(props.dashboard.reportOptions?.language ?? "");

  const setDashboard = useDashboardStore(state => state.setDashboard);

  const { mutate: updateDashboard, isPending } = useUpdateDashboard({
    onSuccess: (res: Dashboard) => {
      setDashboard(res);
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.dashboard-updated"),
        severity: "success",
        life: 3000,
      });
      props.cancel();
    },
  });

  const onGeneralInputChange = (key: keyof Dashboard, value: any) => {
    if (key === "title") {
      setTitle(value);
    } else if (key === "companyShared") {
      setCompanyShared(value);
    }
    props.setNewGeneralSub(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleLanguageChange = (val: string) => {
    setLanguage(val);
    props.setNewGeneralSub(prev => ({
      ...prev,
      reportOptions: {
        ...((prev as Dashboard).reportOptions ?? props.dashboard.reportOptions ?? {}),
        language: val,
      },
    }));
  };

  const handleMigrateReportVersion = () => {
    const newDashboard = { ...props.dashboard };
    if (newDashboard.implementation === "REPORTS_JS") {
      newDashboard.implementation = "REPORTS_JS_V2";
      newDashboard.dashboardTabsJs = newDashboard.dashboardTabsJs.map(tab => ({
        ...tab,
        uuid: v4(),
        componentGroups: tab.componentGroups.map(group => ({
          ...group,
          id: v4(),
          components: group.components.map(component => ({
            ...component,
            id: v4(),
          })),
        })),
      }));
    } else {
      newDashboard.implementation = "REPORTS_JS";
    }
    updateDashboard({ ...newDashboard, overwrite: true });
  };

  return (
    <>
      <h5 className="mb-5">{T("word.general")}</h5>
      <div className="grid align-items-center">
        <span className="p-float-label col-12 md:col-3">
          <Input.Text
            id="input-dashboard-title"
            label={T("word.title")}
            property="title"
            sourceObj={{ title }}
            onChange={onGeneralInputChange}
          />
        </span>

        <span className="p-float-label col-12 md:col-2">
          <Input.Text
            id="input-dashboard-icon-class"
            label={T("word.icon-class")}
            property="iconClass"
            sourceObj={props.dashboard}
            onChange={onGeneralInputChange}
            editable
          />
        </span>

        <span className="p-float-label col-12 md:col-2">
          <Input.List
            id="input-dashboard-report-options-language"
            label={T("word.language")}
            options={availableLanguagesOptions}
            property="language"
            sourceObj={{ language }}
            onChange={(key, value) => handleLanguageChange(value)}
          />
        </span>

        <span className="col-12 md:col-3">
          <Input.Boolean
            id="input-dashboard-company-shared"
            label={T("word.company-shared")}
            property="companyShared"
            sourceObj={{ companyShared }}
            onChange={onGeneralInputChange}
          />
        </span>

        <Button
          label={T("action.migrate-report-version")}
          icon="pi pi-arrow-right-arrow-left"
          className="component-editor-button icon-green"
          loading={isPending}
          onClick={handleMigrateReportVersion}
        />
      </div>
    </>
  );
};

export default GeneralSubForm;
